<template>
    <div
        id="stats"
        v-dark
        class="stats"
    >
        <StatsBanner v-if="examMetadata" :key="answerKey" />
        <ReadinessReportBanner
            v-if="!isLoading"
            :is-dark-mode="isDarkMode"
            class="stats__readiness-report-banner"
            @isBannerVisible="updateIsShowingReadinessReportBanner"
        />
        <ExamDateBanner
            v-if="!isLoading && canSeeExamDateBanner"
            :is-dark-mode="isDarkMode"
            class="stats__exam-date-banner"
            @close="closeExamDateBanner"
        />
        <PocketButton
            v-if="!isLoading && breakpoint === 'black-bear' && additionalQuestionCount && !hasPremiumAccess"
            :is-dark-mode="isDarkMode"
            class="stats__mobile-cta"
            :is-loading="isLoading"
            @click="openUpgradeModal"
        >
            Get {{ additionalQuestionCount }} more questions with Premium
        </PocketButton>
        <SubjectInsights />
        <div class="stats__grid">
            <StudyProgress class="stats__study-progress" />
            <QuizScores class="stats__quiz-scores" />
            <div class="stats__right-column">
                <WeakestConcepts @loaded="trackStatsTabView" />
                <StudyActivity class="stats__study-activity" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import StatsBanner from '@/components/Stats/Banner.vue'
import SubjectInsights from '@/components/SubjectInsights.vue'
import { analyticsModule } from '@/store/analytics/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import QuizScores from '@/components/Stats/QuizScores.vue'
import StudyActivity from '@/components/Stats/StudyActivity.vue'
import StudyProgress from '@/components/Stats/StudyProgress.vue'
import WeakestConcepts from '@/components/Stats/WeakestConcepts.vue'
import ExamDateBanner from '@/components/ExamDateBanner.vue'
import ReadinessReportBanner from '@/components/ReadinessReport/ReadinessReportBanner.vue'
import { screenModule } from '@/store/screen/module'
import { subscriptionModule } from '@/store/subscription/module'
import { upgradeModule } from '@/store/upgrade/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'

const { Button: PocketButton } = UIKit

const isLoading = ref(true)
const isShowingReadinessReportBanner = ref<boolean | null>(null)
const hasTrackedStatsTabView = ref(false)

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const answerKey = computed(() => {
    // keep track of most recent answer serial because if it changes then we reload the Stats Banner
    const answers = Object.values(quizModule.getters.getLatestAnswers({ questionFilter: 'accessible' }))
    const answer = answers[0]
    return `${answer?.questionSerial}_${answers.length}`
})

const additionalQuestionCount = computed(() => {
    if (examMetadata.value) {
        // subtract archived and free questions
        return (examMetadata.value?.itemCount || 0)
            - (examMetadata.value?.archivedCount || 0) - (examMetadata.value?.specialQuestions || 0)
    }
    return 0
})

const hasPremiumAccess = computed(() => {
    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
    return subscriptionModule.getters.getSubscriptionForExamId(currentExam?.objectId)
})

const canSeeExamDateBanner = computed(() => {
    // Hide the exam date banner if we're showing the readiness banner or still loading the readiness banner
    if (isShowingReadinessReportBanner.value || isShowingReadinessReportBanner.value === null) {
        return false
    }

    return userExamMetadataModule.getters.getCanSeeExamDateBanner('Stats')
})

onMounted(async () => {
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userModule.actions.fetchUserData(),
    ])

    const statsEl = document.getElementById('stats')
    if (statsEl) {
        statsEl.scrollIntoView()
        const headEl = document.querySelector('.head')
        headEl?.scrollIntoView()    // For mobile, make sure we can see the header menu
    }

    isLoading.value = false
})

const trackStatsTabView = (weakestConceptsCount: number | null) => {
    if (hasTrackedStatsTabView.value) {
        return
    }

    hasTrackedStatsTabView.value = true
    analyticsModule.actions.amplitudeTrack('Screen View', {
        screenName: 'Stats Tab',
        platform: 'Web',
        weakestConceptsCount: weakestConceptsCount ?? undefined,
    })
}

onUnmounted(() => {
    trackStatsTabView(null)
})

const openUpgradeModal = () => {
    upgradeModule.actions.openUpgradeModal()
}

const closeExamDateBanner = () => {
    userModule.actions.markMessageSeen('examDateBanner')
}

const updateIsShowingReadinessReportBanner = (value: boolean) => {
    isShowingReadinessReportBanner.value = value
}
</script>

<style lang="scss" scoped>
.stats {
    background: $gray-background;
    padding: 0 0 50px;

    &--dark {
        background: $charcoal;
    }

    &__readiness-report-banner,
    &__exam-date-banner {
        width: 975px;
        margin: 21px auto 0;

        @include breakpoint(brown-bear) {
            width: calc(100% - 44px);
        }

        @include breakpoint(black-bear) {
            width: calc(100% - 44px);
            margin-top: 16px;
            margin-bottom: -4px;
        }
    }

    &__mobile-cta {
        width: calc(100% - 44px);
        margin-left: 22px;
        margin-top: 25px;
    }

    &__grid {
        display: grid;
        grid-template-columns: 478px 478px;
        grid-template-rows: auto 1fr;
        justify-content: space-between;
        gap: 18px;
        max-width: 974px;
        margin: 0 auto;

        @include breakpoint(brown-bear) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            grid-template-rows: auto;
            width: calc(100% - 44px);
            max-width: none;
        }

        @include breakpoint(black-bear) {
            grid-template-columns: minmax(0, 1fr);
            width: calc(100% - 44px);
        }
    }

    &__study-progress {
        grid-column: 1;
        grid-row: 1;

        @include breakpoint(brown-bear) {
            grid-column: 1 / -1;
        }
    }

    &__quiz-scores {
        grid-column: 1;
        grid-row: 2;
    }

    // with Weakest Concepts the right column is much taller, and design prefers a gap under Quiz Scores
    // over stretching it to match
    &__grid:has(.weakest-concepts) &__quiz-scores {
        align-self: start;
    }

    // its own column so the Weakest Concepts card's height doesn't stretch the left column's rows
    &__right-column {
        display: flex;
        flex-direction: column;
        gap: 18px;
        grid-column: 2;
        grid-row: 1 / span 2;

        @include breakpoint(brown-bear) {
            grid-row: 2;
        }

        @include breakpoint(black-bear) {
            grid-column: 1;
            grid-row: 3;
        }
    }

    // fills the rest of the column, as it did when it was the grid item stretched across both rows
    &__study-activity {
        flex-grow: 1;
    }
}
</style>
